import React, { useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { useHistory } from "react-router";
import ServiceWrapper from "../../components/ServiceWrapper";
import { api } from "../../utils/api";
import { track } from "../../utils/track";
import { C, Page, Lead, Primary, Ghost, Field, Err, Note, Card, errText } from "./styles";

interface IGroup { id: number; name: string; member_count: number; is_owner: boolean; invite_code?: string | null }

/** 단체 관리 (/my-scores/groups): 단체 만들기, 초대 코드로 들어가기, 나가기 */
const MyScoreGroups: React.FC = () => {
  const history = useHistory();
  const [groups, setGroups] = useState<IGroup[] | null>(null);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/my-score-groups").then(({ data }) => setGroups(data.data)).catch(() => setErr("단체 목록을 불러오지 못했어요."));
  }, []);
  useEffect(load, [load]);

  const run = async (fn: () => Promise<string>) => {
    setBusy(true);
    setErr("");
    setMsg("");
    try { setMsg(await fn()); load(); } catch (e) { setErr(errText(e)); }
    setBusy(false);
  };

  const create = () => run(async () => {
    const { data } = await api.post("/my-score-groups", { name: name.trim() });
    track("myscore:group-create");
    setName("");
    return `「${data.data.name}」 단체를 만들었어요. 초대 코드 ${data.data.invite_code} 를 단체 사람들에게 알려 주세요.`;
  });
  const join = () => run(async () => {
    const { data } = await api.post("/my-score-groups/join", { code: code.trim() });
    track("myscore:group-join");
    setCode("");
    return `「${data.data.name}」 단체에 들어갔어요.`;
  });
  const leave = (g: IGroup) => {
    const q = g.is_owner
      ? `「${g.name}」 단체를 없앨까요? 단체 사람들도 이 단체에 공유된 악보를 더 볼 수 없어요.`
      : `「${g.name}」 단체에서 나갈까요?`;
    if (!window.confirm(q)) return;
    run(async () => { await api.post(`/my-score-groups/${g.id}/leave`); return g.is_owner ? "단체를 없앴어요." : "단체에서 나왔어요."; });
  };
  const copyInvite = async (g: IGroup) => {
    const text = `코드스코어 '${g.name}' 단체 초대 코드: ${g.invite_code}\n코드스코어 > 내 악보 만들기 > 단체 관리에서 코드를 넣어 주세요.`;
    try {
      if ((navigator as any).share) await (navigator as any).share({ text });
      else { await navigator.clipboard.writeText(text); setMsg("초대 글을 복사했어요."); }
    } catch (e) { /* 공유 창을 닫은 경우 */ }
  };

  return (
    <ServiceWrapper isFooterRendered={false}>
      <Page>
        <Back type="button" onClick={() => history.push("/my-scores")}>‹ 내 악보</Back>
        <h1>단체 관리</h1>
        <Lead>밴드·찬양팀·동아리처럼 같이 악보를 보는 사람들을 단체로 묶어요. 내 악보의 <b>공유</b>에서 단체를 고르면 단체 회원만 볼 수 있어요.</Lead>

        {groups && groups.length > 0 && (
          <Groups>
            {groups.map((g) => (
              <li key={g.id}>
                <div>
                  <strong>{g.name}</strong>
                  <span>{g.member_count}명{g.is_owner ? " · 내가 만든 단체" : ""}</span>
                  {g.is_owner && g.invite_code && (
                    <span className="code">초대 코드 <b>{g.invite_code}</b></span>
                  )}
                </div>
                <div className="btns">
                  {g.is_owner && <Ghost type="button" onClick={() => copyInvite(g)}>초대하기</Ghost>}
                  <Ghost type="button" className="danger" disabled={busy} onClick={() => leave(g)}>{g.is_owner ? "없애기" : "나가기"}</Ghost>
                </div>
              </li>
            ))}
          </Groups>
        )}
        {groups && !groups.length && <Note style={{ marginBottom: 16 }}>아직 들어간 단체가 없어요.</Note>}

        {msg && <Card style={{ marginBottom: 16, color: C.ok, fontSize: 15, lineHeight: 1.5 }}>{msg}</Card>}
        {err && <Err style={{ marginBottom: 16 }}>{err}</Err>}

        <Box>
          <h2>초대 코드로 들어가기</h2>
          <Field><input value={code} maxLength={12} placeholder="예: AB12CD" autoCapitalize="characters" onChange={(e) => setCode(e.target.value.toUpperCase())} aria-label="초대 코드" /></Field>
          <Primary type="button" disabled={busy || !code.trim()} onClick={join}>들어가기</Primary>
        </Box>
        <Box>
          <h2>새 단체 만들기</h2>
          <Field><input value={name} maxLength={50} placeholder="예: 우리교회 찬양팀" onChange={(e) => setName(e.target.value)} aria-label="단체 이름" /></Field>
          <Primary type="button" disabled={busy || !name.trim()} onClick={create}>만들기</Primary>
        </Box>
      </Page>
    </ServiceWrapper>
  );
};

export default MyScoreGroups;

const Back = styled.button`
  border: 0;
  background: none;
  color: ${C.muted};
  font-size: 15px;
  padding: 4px 0 10px;
  cursor: pointer;
`;

const Groups = styled.ul`
  list-style: none;
  margin: 0 0 20px;
  padding: 0;
  border: 1px solid ${C.line};
  border-radius: 14px;
  overflow: hidden;
  & > li {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    background: ${C.card};
  }
  & > li + li {
    border-top: 1px solid ${C.line};
  }
  & > li > div:first-of-type {
    flex: 1;
    min-width: 160px;
  }
  & strong {
    display: block;
    font-size: 16px;
  }
  & span {
    display: block;
    margin-top: 2px;
    font-size: 13px;
    color: ${C.dim};
  }
  & span.code b {
    color: ${C.fg};
    letter-spacing: 0.08em;
  }
  & .btns {
    display: flex;
    gap: 6px;
  }
  & .btns > button {
    height: 38px;
    font-size: 14px;
  }
`;

const Box = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 24px;
  & > h2 {
    margin: 0;
    font-size: 15px;
    color: ${C.muted};
  }
`;
